import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { usePerm } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, Empty } from "@/components/PageHeader";
import { TierBadge } from "@/components/TierBadge";
import { api, num, fdatetime, errMsg } from "@/lib/api";

const TiersEditor = () => {
  const [tiers, setTiers] = useState([]);
  useEffect(() => { api.get("/tiers").then((r) => setTiers(r.data)); }, []);
  const upd = (i, k, v) => setTiers(tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  const save = async () => {
    try {
      const r = await api.put("/tiers", tiers.map((t) => ({ ...t, min_points: Number(t.min_points), multiplier: Number(t.multiplier) })));
      setTiers(r.data); toast.success("Livelli aggiornati");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Configurazione livelli" testId="tiers-panel" action={<Button size="sm" data-testid="save-tiers-button" onClick={save} className="bg-blue-600 hover:bg-blue-700">Salva</Button>}>
      <div className="divide-y divide-slate-100">
        {tiers.map((t, i) => (
          <div key={t.key} className="grid grid-cols-[1fr_1fr_1fr] items-center gap-3 px-5 py-3">
            <TierBadge tier={t.key} />
            <div><Label className="text-[11px] text-slate-400">Soglia punti</Label><Input type="number" min="0" data-testid={`tier-${t.key}-min`} value={t.min_points} onChange={(e) => upd(i, "min_points", e.target.value)} className="h-8" /></div>
            <div><Label className="text-[11px] text-slate-400">Moltiplicatore</Label><Input type="number" step="0.05" min="0.1" data-testid={`tier-${t.key}-mult`} value={t.multiplier} onChange={(e) => upd(i, "multiplier", e.target.value)} className="h-8" /></div>
          </div>
        ))}
      </div>
    </Panel>
  );
};

const SegmentPoints = ({ onDone }) => {
  const [segs, setSegs] = useState([]);
  const [f, setF] = useState({ segment_id: "", points: "", reason: "" });
  useEffect(() => { api.get("/segments").then((r) => setSegs(r.data)); }, []);
  const submit = async () => {
    if (!f.segment_id || !Number(f.points) || !f.reason.trim()) return toast.error("Compila tutti i campi");
    try {
      const r = await api.post("/points/segment", { ...f, points: Number(f.points) });
      toast.success(`Punti aggiornati per ${r.data.updated} clienti su ${r.data.total}`);
      setF({ ...f, points: "", reason: "" }); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Assegna punti a un segmento" testId="segment-points-panel">
      <div className="space-y-3 p-5">
        <Select value={f.segment_id} onValueChange={(v) => setF({ ...f, segment_id: v })}>
          <SelectTrigger data-testid="bulk-segment-select"><SelectValue placeholder="Scegli segmento" /></SelectTrigger>
          <SelectContent className="bg-white">{segs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.count})</SelectItem>)}</SelectContent>
        </Select>
        <Input type="number" data-testid="bulk-points-input" placeholder="Punti (negativi per rimuovere)" value={f.points} onChange={(e) => setF({ ...f, points: e.target.value })} />
        <Input data-testid="bulk-reason-input" placeholder="Causale" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
        <Button data-testid="bulk-points-submit" onClick={submit} className="w-full bg-blue-600 hover:bg-blue-700">Applica al segmento</Button>
      </div>
    </Panel>
  );
};

export default function Loyalty() {
  const [mvs, setMvs] = useState(null);
  const [type, setType] = useState("all");
  const usePermGifts = usePerm("gifts");
  const load = () => api.get("/points/movements", { params: { type: type === "all" ? "" : type } }).then((r) => setMvs(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <PageHeader title="Punti e livelli fedeltà" subtitle="Soglie dei livelli, movimenti e assegnazioni massive" />
      <div className="grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <div className="space-y-6"><TiersEditor /><SegmentPoints onDone={load} /></div>
        <Panel title="Registro movimenti punti" testId="movements-panel" action={<div className="flex items-center gap-2">
          {usePermGifts !== "none" && <Link to="/premi?tab=redemptions" data-testid="movements-gifts-link" className="text-xs text-blue-600 hover:underline">Filtra riscatti per premio →</Link>}
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-8 w-36" data-testid="movements-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tutti</SelectItem><SelectItem value="earn">Accumulo</SelectItem><SelectItem value="redeem">Riscatto</SelectItem><SelectItem value="manual">Manuale</SelectItem>
            </SelectContent>
          </Select></div>}>
          <div className="max-h-[640px] overflow-y-auto">
            {mvs && mvs.length === 0 && <Empty text="Nessun movimento." />}
            {(mvs || []).map((m) => (
              <div key={m.id} className="flex items-center justify-between border-b border-slate-100 px-5 py-3 last:border-0" data-testid="movement-row">
                <div><div className="text-sm font-medium text-slate-800">{m.customer_name}</div><div className="text-xs text-slate-400">{m.reason} · {fdatetime(m.created_at)}</div></div>
                <span className={`font-mono text-sm ${m.points >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{m.points >= 0 ? "+" : ""}{num(m.points)}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
